import React, { Key, MutableRefObject, ReactNode, useEffect, useMemo, useState } from 'react';

import { css } from '@emotion/css';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { Radio } from 'antd';
import { TableLocale, TableRowSelection } from 'antd/lib/table/interface';
import { useDebounce } from 'use-debounce/lib';
import { ZipCelXSheet, ZipCelXCell } from 'zipcelx';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { MultiSelectOption } from 'components/AsyncSelect/types';
import { getPillFacetOptions, getSearchFacetMultiSelectOptions } from 'components/AsyncSelect/useAsyncMultiSelectProps';
import { ActionButton } from 'components/Button/ActionButton';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { Container } from 'components/Container';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { PageHeader } from 'components/PageHeader';
import { ResultCount } from 'components/ResultCount';
import { SearchInput } from 'components/SearchInput';
import { Table } from 'components/Table';

import { usePromise } from 'hooks/usePromise';

import { arrFilterByValues } from 'utils/array';
import { captureAndShowError } from 'utils/error';
import { objGet, objRemoveUndefinedValues } from 'utils/object';
import { useHashState } from 'utils/routes';
import { getAllRecordsFromSearchIndex, searchIndex } from 'utils/search/search';
import { SearchRequest, DEFAULT_HITS_PER_PAGE } from 'utils/search/types';
import { PersistedColumn, ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { useUserViewSettingsState } from 'utils/tableAndSidePaneSettings/useUserViewSettingsState';
import { ExportColumn, withSortedColumn } from 'utils/tables';
import { timePromise, track, TrackEvent } from 'utils/track';

import { SearchFrameHashState } from 'types/hash-state';
import { ObjectWithId } from 'types/search-collections';

import { Counter } from '../../Counter';
import { RadioGroup } from '../../Radio';
import { ColumnChooserSection } from '../ColumnChooserSection';
import { TableSplitPage } from '../TableSplitPage';
import { SearchFrameSelectionInfo } from './SearchFrameSelectionInfo';
import {
  ExternalFacet,
  FacetRenderType,
  SearchFrameContext,
  SearchFrameRowSelection,
  SearchFrameSelection,
  ValueFacet,
} from './types';
import { flattenExportColumns, getFiltersWithDefaultFacetValues } from './utils';

export interface SearchFrameProps<HitT> {
  indexName: SearchIndexName;
  /** large title of the search page */
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  headerDescription?: string | ReactNode;
  queryPlaceholder: string;
  valueFacets?: ValueFacet<HitT>[];
  externalFacets?: ExternalFacet[];
  columns: ExportColumn<HitT>[];
  // function to run on the list of hits before displaying
  hitFn?: (hits: HitT[]) => Any[];
  /** component that shows besides search, e.g New Order CTA button */
  headerActions?: ReactNode | ((searchFrameContext: SearchFrameContext<HitT>) => ReactNode);
  /**
   * Optional toolbar above the filters to save filter sets
   */
  savedFiltersToolbar?: ReactNode;
  tableRowKey?: keyof HitT;
  /** if tableSettingsKey is set, then column customization is shown  */
  tableColumnsSettingKey?: ViewSettingKey;
  tableRowSelection?: TableRowSelection<HitT> | 'selectAll';
  contextRef?: MutableRefObject<SearchFrameContext<HitT> | undefined>;
  sidePane?: (props: { record: HitT | null; searchIndexReload: () => void }) => ReactNode;
  locale?: TableLocale & { notFoundText?: React.ReactNode };
  defaultSortBy?: SearchFrameHashState['sortBy'];
  secondarySortBy?: SearchFrameHashState['secondarySortBy'];
}

export function SearchFrame<HitT extends ObjectWithId>({
  indexName,
  title,
  subtitle,
  headerDescription,
  queryPlaceholder,
  valueFacets = [],
  externalFacets = [],
  columns,
  hitFn,
  headerActions,
  savedFiltersToolbar,
  tableRowKey = 'id',
  tableColumnsSettingKey,
  tableRowSelection,
  contextRef,
  sidePane,
  locale,
  defaultSortBy,
  secondarySortBy,
}: SearchFrameProps<HitT>) {
  const [hashState, updateHashState] = useHashState<SearchFrameHashState>();
  const [debouncedQuery] = useDebounce(hashState.query, 500);
  const [focusedRecord, setFocusedRecord] = useState<HitT | null>(null);
  // Don't use the set selections of the use states, use the other functions instead for safety
  const [selectedRows, setSelectedRows] = useState<HitT[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const persistableColumns: PersistedColumn<HitT>[] = (columns as Any[]).filter((col) => col && !!col.settingKey);
  const defaultColumns = persistableColumns.filter((col) => !col.optional && !col.exportOnly);
  const [visibleColumnKeys, setVisibleColumnKeys] = useUserViewSettingsState(tableColumnsSettingKey, defaultColumns);

  const downloadColumns = tableColumnsSettingKey
    ? arrFilterByValues(persistableColumns, 'settingKey', visibleColumnKeys)
    : columns.filter((col) => !(col as PersistedColumn<HitT>).optional);

  const visibleColumns = downloadColumns.filter((col) => !col.exportOnly);

  function setSelectedAll(newIsAllSelected: boolean) {
    if (isAllSelected === newIsAllSelected) {
      return;
    }
    if (newIsAllSelected) {
      setIsAllSelected(true);
      setSelectedRows([]);
    } else {
      setIsAllSelected(false);
      setSelectedRows([]);
    }
  }

  function setSelection(newSelectedRows: HitT[]) {
    if (isAllSelected) {
      setSelectedAll(false);
    } else {
      setSelectedRows(newSelectedRows);
    }
  }

  function getRowKey(record: HitT): Key {
    return record[tableRowKey] as unknown as Key;
  }

  // update default filter values (if any)
  // don't mutate the hashState object directly
  const filters = getFiltersWithDefaultFacetValues(hashState.where, valueFacets);
  const pillFacets = valueFacets.filter((f) => !f.hidden && f.renderType === FacetRenderType.Pill);
  const multiSelectFacets = valueFacets.filter((f) => !f.hidden && f.renderType !== FacetRenderType.Pill);

  const searchRequest: SearchRequest = {
    indexName,
    query: debouncedQuery?.trim() || '',
    filters,
    // only the pillFacet counts are rendered on page, multiSelectFacets are lazy loaded
    facetFields: pillFacets.map((f) => f.field),
    page: hashState.page || 0,
    sortBy: hashState.sortBy || defaultSortBy,
    secondarySortBy: secondarySortBy ?? undefined,
  };

  const {
    data: searchIndexResponse,
    isLoading,
    isReloading,
    reload: searchIndexReload,
  } = usePromise(
    () =>
      timePromise(searchIndex<HitT>(searchRequest), (resDurationMs) => {
        track(TrackEvent.Components_SearchFrameQuery, {
          indexName,
          searchQuery: searchRequest.query ?? '',
          searchQueryLength: searchRequest.query?.length ?? 0,
          facetValueFilterKeys: Object.keys(searchRequest.filters || {}),
          sortByField: searchRequest.sortBy?.field,
          sortByDir: searchRequest.sortBy?.order,
          resDurationMs,
        });
      }),
    [],
    {
      cacheKey: `searchframe/${indexName}:${JSON.stringify(searchRequest)}`,
      onError: (err) =>
        captureAndShowError(err, 'Error while searching', { notificationKey: `searchframe/${indexName}` }),
    },
  );

  const records: HitT[] = useMemo(() => {
    const hits = searchIndexResponse?.hits || [];
    return hitFn ? hitFn(hits) : hits;
  }, [searchIndexResponse, hitFn]);

  // update external value facets (if any)
  for (const facet of externalFacets) {
    if (facet.defaultValue && !hashState?.extFilter?.[facet.field]) {
      updateHashState({
        extFilter: objRemoveUndefinedValues({
          ...hashState.extFilter,
          [facet.field]: [facet.defaultValue],
        }),
      });
    }
  }

  // If records have changed, update focused row accordingly
  useEffect(() => {
    if (sidePane) {
      const recordMatch = focusedRecord
        ? records.find((record) => record[tableRowKey] === focusedRecord[tableRowKey])
        : undefined;

      // focus on new record with same id
      if (focusedRecord && recordMatch && focusedRecord !== recordMatch) {
        setFocusedRecord(recordMatch);
      }
      // if nothing matches, then focus on first row that matches result set
      else if (!recordMatch && records.length > 0) {
        setFocusedRecord(records[0]);
      }
      // otherwise clear focusedRecord if no records
      else if (records.length === 0) {
        setFocusedRecord(null);
      }
    }
  }, [focusedRecord, records, sidePane, tableRowKey]);

  const rowSelection: (TableRowSelection<HitT> & SearchFrameRowSelection<HitT>) | undefined =
    tableRowSelection === 'selectAll'
      ? {
          selectedAll: isAllSelected,
          preserveSelectedRowKeys: true,
          selectedRowKeys: isAllSelected
            ? searchIndexResponse?.hits.map((hit) => getRowKey(hit))
            : selectedRows.map((hit) => getRowKey(hit)),
          selectedRows,
          onSelect: (record, selected) => {
            if (!selected) {
              setSelection(selectedRows.filter((value) => getRowKey(value) !== getRowKey(record)));
            } else {
              setSelection([...selectedRows, record]);
              setFocusedRecord(record);
            }
          },
          onChange: (_, newSelectedRows) => {
            setSelection(newSelectedRows);
          },
        }
      : tableRowSelection;

  let selection: SearchFrameSelection<HitT> | undefined;
  if (rowSelection?.selectedAll) {
    selection = {
      type: 'filter',
      filters: searchRequest.filters,
      count: searchIndexResponse?.nbHits ?? 0,
    };
  } else if (rowSelection?.selectedRowKeys?.length) {
    const latestRowsById = new Map(searchIndexResponse?.hits.map((hit) => [hit.id, hit]));

    if (selectedRows && selectedRows.length > 0)
      selection = {
        type: 'list',
        // use latest loaded data for row if in current page, otherwise fallback to stale value if selection in a previous page
        items: selectedRows.map((row) => latestRowsById.get(row.id) ?? row),
        count: selectedRows.length,
      };
  }

  const joinIfArray = (val: string | boolean | string[] | undefined) => (Array.isArray(val) ? val.join(',') : val);

  const renderPillFilter = (facet: ValueFacet<HitT>) => {
    const selectionProps = getPillFacetOptions({
      facetValues: searchIndexResponse?.facets?.[facet.field] || [],
      labelFormatFn: facet.labelFormatFn,
      staticOptions: facet.options,
    })();

    const currentValue = joinIfArray(filters[facet.field] ?? facet.defaultValue);

    /** Don't show filter while loading or if there's only one option */
    if (selectionProps.options.length <= 1) {
      return null;
    }

    return (
      <FlexSpace
        className={css`
          margin-top: 0px;
          margin-bottom: 24px;
        `}
      >
        <RadioGroup
          value={currentValue}
          onChange={(ev) => {
            updateHashState({
              where: objRemoveUndefinedValues({
                ...hashState.where,
                [facet.field]: ev.target.value.split(','),
              }),
              page: 0,
            });
            setSelectedAll(false);
          }}
        >
          {selectionProps.options.map((option) => (
            <Radio.Button key={JSON.stringify(option.value)} value={joinIfArray(option.value)}>
              {option.label}
              <Counter
                className={css`
                  margin-left: 6px;
                `}
                variant="primary"
                selected={joinIfArray(currentValue) === joinIfArray(option.value)}
                value={option.count ?? 0}
              />
            </Radio.Button>
          ))}
        </RadioGroup>
      </FlexSpace>
    );
  };

  const canDownload = !!(!rowSelection || rowSelection.selectedAll || rowSelection.selectedRowKeys?.length);

  const clearSelection = () => {
    if (rowSelection) {
      if (rowSelection.onSelectNone) {
        rowSelection.onSelectNone();
      } else {
        rowSelection.onChange?.([], []);
      }
      selection = undefined;
    }
  };

  const searchFrameContext: SearchFrameContext<HitT> = {
    hashState,
    searchRequest,
    searchIndexResponse,
    selection,
    searchIndexReload,
    clearSelection,
    canDownload,
    getDownloadData: () =>
      fetchDownloadXSheet({
        indexName,
        columns: downloadColumns,
        rowSelection,
        searchRequest,
      }),
  };

  const searchHitKeys = searchIndexResponse?.hits.map(getRowKey);

  if (contextRef) {
    contextRef.current = searchFrameContext;
  }

  return (
    <Container>
      <PageHeader
        title={title}
        subtitle={subtitle}
        description={headerDescription}
        headerActions={typeof headerActions === 'function' ? headerActions(searchFrameContext) : headerActions}
      />

      {savedFiltersToolbar ? (
        <div
          className={css`
            margin-bottom: 16px;
          `}
        >
          {savedFiltersToolbar}
        </div>
      ) : null}

      {pillFacets.map((facet) => renderPillFilter(facet))}

      <FilterBarBox dividerLine>
        {multiSelectFacets.map((facet, idx) => (
          <AsyncMultiSelect
            key={idx}
            label={facet.title}
            icon={facet.icon}
            mode={facet.filterType}
            popoverClassName={css`
              min-width: 250px;
            `}
            selectProps={getSearchFacetMultiSelectOptions({
              facetField: facet.field,
              searchRequest,
              labelFormatFn: facet.labelFormatFn,
              sortBy: facet.sortBy,
              staticOptions: facet.options as MultiSelectOption[],
            })}
            triggerButton={facet.triggerButton || undefined}
            selectedValues={filters[facet.field] ?? []}
            queryPlaceholder={facet.queryPlaceholder}
            onSelectedValuesChange={(values) => {
              updateHashState({
                where: objRemoveUndefinedValues({
                  ...hashState.where,
                  [facet.field]: values.length > 0 ? values : undefined,
                }),
                page: 0,
              });
            }}
          />
        ))}
        {hashState.where && Object.keys(hashState.where).length > 0 ? (
          <ActionButton onClick={() => updateHashState({ where: undefined, query: undefined })} label="Clear All" />
        ) : null}
        <FlexSpacer />
        {externalFacets.map((facet, idx) => (
          <AsyncMultiSelect
            key={valueFacets.length + idx}
            label={facet.title}
            icon={facet.icon}
            mode={facet.filterType}
            selectProps={{ options: facet.options ?? [] }}
            selectedValues={hashState.extFilter?.[facet.field] ?? []}
            onSelectedValuesChange={(values) => {
              updateHashState({
                extFilter: objRemoveUndefinedValues({
                  ...hashState.extFilter,
                  [facet.field]: values.length > 0 ? values : undefined,
                }),
                page: 0,
              });
            }}
          />
        ))}
        <SearchInput
          placeholder={queryPlaceholder}
          query={hashState.query}
          onQueryChange={(query) => updateHashState({ query, page: 0 })}
          disabled={isAllSelected}
        />
        <ResultCount count={searchIndexResponse?.nbHits || 0} dataTestId="search-frame-result-count" />
      </FilterBarBox>

      <ConditionalWrapper
        condition={!!sidePane}
        wrapper={(children) => (
          <TableSplitPage
            left={children}
            right={sidePane!({
              record: focusedRecord,
              searchIndexReload,
            })}
          />
        )}
      >
        <>
          <FlexSpace
            gap={16}
            className={css`
              justify-content: space-between;
              align-items: center;
              position: relative;
              // margin and min-height to account for the column chooser button
              min-height: ${tableColumnsSettingKey ? '40px' : 'auto'};
              margin-right: ${tableColumnsSettingKey ? '100px' : 0};
            `}
          >
            <SearchFrameSelectionInfo
              isSelectedPage={
                !isAllSelected &&
                !debouncedQuery &&
                searchIndexResponse &&
                selectedRows.length === searchHitKeys?.length &&
                selectedRows.every((element, _) => searchHitKeys.includes(getRowKey(element))) &&
                searchIndexResponse.nbHits > selectedRows.length
              }
              isSelectedAll={rowSelection?.selectedAll}
              selectedRowsLength={selectedRows.length}
              totalHitsLength={searchIndexResponse?.nbHits}
              onClear={() => rowSelection?.onSelectNone?.() ?? rowSelection?.onChange?.([], [])}
              onSelectAll={() => setSelectedAll(true)}
            />

            {tableColumnsSettingKey ? (
              <div
                className={css`
                  // offset to account for the margin on the flex space
                  position: absolute;
                  right: -100px;
                `}
              >
                <ColumnChooserSection
                  tableKey={tableColumnsSettingKey}
                  columns={persistableColumns}
                  visibleColumnKeys={visibleColumnKeys}
                  setVisibleColumnKeys={setVisibleColumnKeys}
                />
              </div>
            ) : null}
          </FlexSpace>
          <Table
            stickyHeader
            scroll={{ x: true }}
            columns={withSortedColumn(visibleColumns, searchRequest.sortBy?.field, searchRequest.sortBy?.order)}
            isLoading={isLoading || isReloading}
            data={records}
            rowKey={tableRowKey as string}
            rowSelection={rowSelection}
            rowClassName={(record: HitT) =>
              [sidePane ? 'focusable-row' : '', record === focusedRecord ? 'focused-row' : ''].join(' ')
            }
            onRow={
              sidePane
                ? (record) => ({
                    onClick: () => setFocusedRecord(record),
                  })
                : undefined
            }
            pagination={
              searchIndexResponse && searchIndexResponse.nbPages > 1
                ? {
                    simple: true,
                    // NOTE: Pagination component uses 1-based index, search index returns 0 based index
                    onChange: (oneIndexedPageNum) => updateHashState({ page: oneIndexedPageNum - 1 }),
                    pageSize: DEFAULT_HITS_PER_PAGE,
                    current: searchIndexResponse.page + 1,
                    total: searchIndexResponse.nbPages * DEFAULT_HITS_PER_PAGE,
                  }
                : false
            }
            onChange={(_pagination, _filters, sorter, { action }) => {
              // !Array.isArray is there to typeguard into one sorter field
              // all our search frame tables use single sorting, so this is okay
              if (action === 'sort' && !Array.isArray(sorter)) {
                updateHashState({
                  sortBy: { field: sorter.field as string, order: sorter.order === 'descend' ? 'desc' : 'asc' },
                  page: 0,
                });
              }
            }}
            locale={{
              ...locale,
              emptyText:
                (hashState.where && Object.keys(hashState.where).length) ||
                (hashState.query && hashState.query?.length > 0)
                  ? locale?.notFoundText
                  : locale?.emptyText,
            }}
          />
        </>
      </ConditionalWrapper>
    </Container>
  );
}

/** query core-api to fetch filtered/selected rows */
export async function fetchDownloadXSheet<HitT>({
  indexName,
  columns,
  rowSelection,
  searchRequest,
}: {
  indexName: SearchIndexName;
  columns: ExportColumn<HitT>[];
  rowSelection: SearchFrameRowSelection<HitT> | undefined;
  searchRequest: SearchRequest;
}): Promise<ZipCelXSheet> {
  const expandedColumns = columns.reduce(
    (flattened: ExportColumn<HitT>[], column) =>
      // expand columns with children into multiple export columns
      flattened.concat(column.children ? column.children : [column]),
    [],
  );

  const exportColumns = flattenExportColumns(expandedColumns).filter((column) => column.title && column.dataIndex);

  let exportData: Any[];
  // if selected all then get from search index, otherwise use selected rows
  if (!rowSelection || !rowSelection.selectedRows || rowSelection.selectedAll) {
    const fieldsToRetrieve: string[] = Array.from(new Set(exportColumns.map((column) => column.dataIndex)));

    exportData = await getAllRecordsFromSearchIndex({
      indexName,
      filters: searchRequest.filters,
      fieldsToRetrieve,
    });
  } else {
    exportData = rowSelection.selectedRows;
  }

  const headerRow = exportColumns.map((column) => ({
    value: column.title,
    type: column.exportType ? column.exportType : 'string',
  }));

  const dataRows = exportData.map((hit) =>
    exportColumns.map((column) => {
      const value =
        typeof column.exportValue === 'function'
          ? column.exportValue(objGet(hit, column.dataIndex))
          : objGet(hit, column.dataIndex);

      return {
        value: typeof value !== 'object' ? value : '',
        type: column.exportType ? column.exportType : typeof value === 'number' ? 'number' : 'string',
      } as ZipCelXCell;
    }),
  );

  const sheet: ZipCelXSheet = {
    data: [headerRow, ...dataRows],
  };

  return sheet;
}
